/* eslint-disable @typescript-eslint/no-explicit-any */
import { Reducer, AnyAction } from 'redux'
import { createSelector, PayloadAction } from '@reduxjs/toolkit'
import { Filter, keplerGlReducer as _keplerGlReducer } from '@carto/kepler.gl/reducers'
import { ALL_FIELD_TYPES } from '@carto/kepler.gl/constants'
import { ActionTypes, LayerConfigChangeUpdaterAction } from '@carto/kepler.gl/actions'
import { Layer } from '@carto/kepler.gl/layers'
import { LayerClasses, timeToUnixMilli } from '@carto/kepler.gl'
import * as cartoReactApi from '@carto/react-api'
import { SourceFilters } from '@carto/react-redux'
// @ts-expect-error TODO: Improve types.
import { UnconnectedKeplerGlProps } from '@carto/kepler.gl/components'
import TileLayer from '../ui/KeplerGl/layers/TileLayer'
import QuadbinLayer from '../ui/KeplerGl/layers/QuadbinLayer'
import HeatMapLayer from '../ui/KeplerGl/layers/HeatMapLayer'
import GeojsonLayer from '../ui/KeplerGl/layers/GeojsonLayer'
import HeatmapTileLayer from '../ui/KeplerGl/layers/HeatmapTileLayer'
import ClusterTileLayer from '../ui/KeplerGl/layers/ClusterTileLayer'
import IconLayer from '../ui/KeplerGl/layers/IconLayer'
import { LAYER_GROUPS } from '../ui/KeplerGl/defaultMapStyles'
import Icon3D from 'assets/icons/layers/3D.svg?react'
import IconArc from 'assets/icons/layers/arc.svg?react'
import IconCluster from 'assets/icons/layers/cluster.svg?react'
import IconGeojson from 'assets/icons/layers/geojson.svg?react'
import IconGrid from 'assets/icons/layers/grid.svg?react'
import IconHexagon from 'assets/icons/layers/hexagon.svg?react'
import IconHexagonId from 'assets/icons/layers/hexagonId.svg?react'
import IconIcon from 'assets/icons/layers/icon.svg?react'
import IconLine from 'assets/icons/layers/line.svg?react'
import IconPoint from 'assets/icons/layers/point.svg?react'
import IconS2 from 'assets/icons/layers/s2.svg?react'
import IconTrip from 'assets/icons/layers/trip.svg?react'
import { MapType } from 'features/maps/types'
import { updateAllLayerDomainData } from '@carto/kepler.gl/utils'
import { TilejsonResult } from '@carto/api-client'
import {
  getAttributesFromTilesetDataset,
  getFieldsTypesFromTilesetDataset,
  getZoomRangeFromTilesetDataset,
  setAggregationResLevelFromTilesetDataset,
  setSchemaFromTilesetDataset,
  setTilejsonFromTilesetDataset
} from './builderSliceUtils'
import { RootState } from 'app/state/store'
import { LAYER_SIDE_PANEL } from '../ui/builderConstants'
import H3Layer from '../ui/KeplerGl/layers/H3Layer'
import {
  setIsLayerLoadingTiles,
  setGPUKeplerFilters,
  updateTilesetDatasetAttributeStats,
  updateTilesetDatasetResolution,
  updateTilesetDatasetTilejson,
  updateTilesetHexColumnStats
} from './keplerGlActions'
import { RequestStatus } from 'features/common'
import UnknownLayer from '../ui/KeplerGl/layers/UnknownLayer'
import { AttributeStats, KeplerTable, KeplerLayerClasses, LayerTypes } from '../types'
import { SchemaField } from 'features/data-explorer/types'
import RasterTileLayer from '../ui/KeplerGl/layers/RasterTileLayer'

const keplerGlReducer: Reducer<{ map: typeof UnconnectedKeplerGlProps }, AnyAction> = _keplerGlReducer
  .initialState({
    uiState: {
      activeSidePanel: '',
      currentModal: null,
      mapControls: {
        visibleLayers: {
          show: true
        },
        mapLegend: {
          show: true
        },
        toggle3d: {
          show: false
        },
        splitMap: {
          show: false
        },
        mapDraw: {
          show: false
        },
        mapLocale: {
          show: false
        }
      }
    },
    mapStyle: {
      mapStyles: [],
      // The styleType should be empty to avoid load an incorrect MapComponent before getting the map configuration from the backend.
      // This way, we avoid unnecessary destruction of the Deck instance and we solve problems like https://github.com/visgl/deck.gl/issues/6577
      styleType: '',
      visibleLayerGroups: LAYER_GROUPS.reduce((acc, current) => {
        acc[current.slug] = current.defaultVisibility
        return acc
      }, {} as Record<string, boolean>)
    },
    visState: {
      layerClasses: getLayerClasses()
    }
  })
  .plugin({
    [ActionTypes.LAYER_CONFIG_CHANGE]: (
      state: typeof UnconnectedKeplerGlProps,
      action: LayerConfigChangeUpdaterAction
    ) => {
      const { oldLayer } = action

      if (action.newConfig && action.newConfig.isConfigActive) {
        state.visState.layers.forEach((l: Layer) => {
          if (l.id !== oldLayer.id) {
            l.config.isConfigActive = false
          }
        })
      }

      return state
    },
    // Deck-gl rename lngLat to coordinate in pickInfo in 6.3.0 version so this action is necessary to place the popup in the correct place when a layer is clicked
    [ActionTypes.LAYER_CLICK]: (
      state: typeof UnconnectedKeplerGlProps,
      action: { info: { picked: boolean; coordinate: Array<number> } }
    ) => {
      if (action?.info?.picked && action?.info?.coordinate) {
        state.visState.clicked.lngLat = action.info.coordinate
      }
      return state
    },
    [setGPUKeplerFilters.type]: (
      state: typeof UnconnectedKeplerGlProps,
      action: PayloadAction<{ id: string; filters: SourceFilters }>
    ) => {
      const { id, filters } = action.payload
      const dataset = state.visState.datasets[id]

      if (dataset) {
        const { filterRange, updateTriggers, getFilterValue } = (cartoReactApi as any).getDataFilterExtensionProps(
          filters
        )
        dataset.gpuFilter.filterRange = filterRange
        // JSON.stringify is necessary because filterValueUpdateTriggers wait for string
        dataset.gpuFilter.filterValueUpdateTriggers = JSON.stringify(updateTriggers)
        dataset.gpuFilter.filterValueAccessor = getFilterValueAccessor(dataset, filters, getFilterValue)
      }
      return {
        ...state,
        // fixedDomain to true is to tell kepler that doesn't update the layer domain
        visState: updateAllLayerDomainData(state.visState, id, { fixedDomain: true } as Filter)
      }
    },
    [updateTilesetDatasetAttributeStats.type]: (
      state: typeof UnconnectedKeplerGlProps,
      action: PayloadAction<{ datasetId: string; stats: AttributeStats }>
    ) => {
      const { datasetId, stats } = action.payload
      const dataset: KeplerTable | undefined = state.visState.datasets[datasetId]
      const fields = dataset && getFieldsTypesFromTilesetDataset(dataset)

      if (dataset && fields) {
        const attributes = getAttributesFromTilesetDataset(dataset)
        const currentAttributeIndx = attributes.findIndex((attr) => attr.attribute === stats.attribute)

        if (currentAttributeIndx >= 0) {
          attributes.splice(currentAttributeIndx, 1)
        }
        attributes.push({
          ...stats,
          type: fields[stats.attribute]
        } as AttributeStats)
      }
      return {
        ...state,
        visState: updateAllLayerDomainData(state.visState, datasetId)
      }
    },
    [updateTilesetHexColumnStats.type]: (
      state: typeof UnconnectedKeplerGlProps,
      action: PayloadAction<{ datasetId: string; stats: AttributeStats }>
    ) => {
      const { datasetId, stats } = action.payload
      const dataset: KeplerTable | undefined = state.visState.datasets[datasetId]

      if (dataset) {
        dataset.metadata.hexColumnStats = {
          ...dataset.metadata.hexColumnStats,
          [stats.attribute]: stats
        }
      }
      return {
        ...state,
        visState: updateAllLayerDomainData(state.visState, datasetId)
      }
    },
    [setIsLayerLoadingTiles.type]: (
      state: typeof UnconnectedKeplerGlProps,
      action: PayloadAction<{ layerId: string; isLoadingTiles: boolean }>
    ) => {
      const { layerId, isLoadingTiles } = action.payload
      const layer = state.visState.layers.find((l) => l.id === layerId)

      if (layer) {
        layer.isLoadingTiles = isLoadingTiles
      }

      return {
        ...state,
        visState: {
          ...state.visState,
          layers: [...state.visState.layers]
        }
      }
    },
    [updateTilesetDatasetTilejson.type]: (
      state: typeof UnconnectedKeplerGlProps,
      action: PayloadAction<{
        datasetId: string
        tilejson?: TilejsonResult | null
        schema?: SchemaField[] | null
      }>
    ) => {
      const { datasetId, tilejson, schema } = action.payload
      const dataset = state.visState.datasets[datasetId]

      if (dataset) {
        setTilejsonFromTilesetDataset(dataset, tilejson)
        if (schema !== undefined) {
          setSchemaFromTilesetDataset(dataset, schema ?? undefined)
        }
      }
      return {
        ...state,
        visState: updateAllLayerDomainData(state.visState, datasetId)
      }
    },
    [updateTilesetDatasetResolution.type]: (
      state: typeof UnconnectedKeplerGlProps,
      action: PayloadAction<{
        datasetId: string
        aggregationResLevel: number
      }>
    ) => {
      const { datasetId, aggregationResLevel } = action.payload
      const dataset = state.visState.datasets[datasetId]

      if (dataset) {
        setAggregationResLevelFromTilesetDataset(dataset, aggregationResLevel)
      }
      return {
        ...state,
        visState: updateAllLayerDomainData(state.visState, datasetId)
      }
    }
  })

const getFilterValueAccessor = (dataset, filters, getFilterValue) => () => () => (d) => {
  let feature

  if (dataset.metadata?.isDynamicSpatialIndex) {
    feature = d.properties
  } else if (dataset.metadata?.type === MapType.tileset || dataset.metadata?.type === MapType.raster) {
    // TODO this code is a workaround to solve this problem https://github.com/CartoDB/cloud-native/pull/8908
    // but it doesn't work with dynamic spatial index so we need to find another approach

    feature = Object.keys(filters).reduce((accum, columnName) => {
      const field = dataset.getColumnField(columnName)
      // same logic as kepler.gl`s `maybeToDate`
      const isTime = field && (field.type === ALL_FIELD_TYPES.timestamp || field.type === ALL_FIELD_TYPES.date)
      const value = d.properties[columnName]
      // ignore field.format as in case of tileset, it's bogus value and thus let moment try all known formats
      accum[columnName] = isTime ? timeToUnixMilli(value) : value
      return accum
    }, {})
  } else {
    feature = Object.keys(filters).reduce((accum, current) => {
      accum[current] = dataset.getValue(current, d.properties ? d.properties.index : d.index)
      return accum
    }, {})
  }
  return getFilterValue(feature)
}

export function getTilesetLayerClasses(): KeplerLayerClasses {
  return {
    [LayerTypes.Unknown]: UnknownLayer,
    [LayerTypes.Tileset]: TileLayer,
    [LayerTypes.Quadbin]: QuadbinLayer,
    [LayerTypes.H3]: H3Layer,
    [LayerTypes.HeatmapTile]: HeatmapTileLayer,
    [LayerTypes.ClusterTile]: ClusterTileLayer,
    [LayerTypes.Raster]: RasterTileLayer,

    // layerClasses.mvt is deprecated. We keep it for old maps
    mvt: TileLayer
  } as unknown as KeplerLayerClasses
}

export function getLayerClasses(): KeplerLayerClasses {
  let layerClasses = LayerClasses

  Object.entries(getTilesetLayerClasses()).forEach(([type, klass]) => {
    layerClasses[type] = klass
  })
  ;(layerClasses as Record<string, unknown>).heatmap = HeatMapLayer
  layerClasses.geojson = GeojsonLayer
  layerClasses.icon = IconLayer
  layerClasses = redefineLayersIcons(layerClasses)
  return layerClasses as unknown as KeplerLayerClasses
}

function redefineLayersIcons(layerClasses: any) {
  redefineLayerIcon(layerClasses.point.prototype, IconPoint)
  redefineLayerIcon(layerClasses.arc.prototype, IconArc)
  redefineLayerIcon(layerClasses.line.prototype, IconLine)
  redefineLayerIcon(layerClasses.grid.prototype, IconGrid)
  redefineLayerIcon(layerClasses.hexagon.prototype, IconHexagon)
  redefineLayerIcon(layerClasses.geojson.prototype, IconGeojson)
  redefineLayerIcon(layerClasses.cluster.prototype, IconCluster)
  redefineLayerIcon(layerClasses.icon.prototype, IconIcon)
  redefineLayerIcon(layerClasses.hexagonId.prototype, IconHexagonId)
  redefineLayerIcon(layerClasses['3D'].prototype, Icon3D)
  redefineLayerIcon(layerClasses.trip.prototype, IconTrip)
  redefineLayerIcon(layerClasses.s2.prototype, IconS2)
  return layerClasses
}

function redefineLayerIcon(layerClassesPrototype: any, icon: React.FunctionComponent) {
  Object.defineProperty(layerClassesPrototype, 'layerIcon', {
    get: function () {
      return icon
    }
  })
}

export const getOpenLayerConfig = createSelector(
  (state: RootState) => state.keplerGl?.map?.visState.layers,
  (layers) => {
    if (layers && layers.length > 0) {
      return layers.find((l: Layer) => l.config.isConfigActive)
    }
  }
)

export const keplerIsLoaded = createSelector(
  (state: RootState) => state.keplerGl,
  (keplerGl) => {
    return Object.prototype.hasOwnProperty.call(keplerGl, 'map')
  }
)

export const imageDataUri = createSelector(
  (state: RootState) => state.keplerGl?.map?.uiState.exportImage?.imageDataUri,
  (imageDataUri) => {
    return imageDataUri
  }
)

export const exportImageExporting = createSelector(
  (state: RootState) => state.keplerGl?.map?.uiState.exportImage?.exporting,
  (exporting) => {
    return exporting
  }
)

export const getFiltersByDatasetId = createSelector(
  (state: RootState) => state.keplerGl?.map?.visState.filters,
  (_: unknown, dataId: string) => dataId,
  (filters, dataId) => {
    return filters.filter((f: { dataId: string | string[] }) => f.dataId.indexOf(dataId) >= 0)
  }
)

export const getDatasets = createSelector(
  (state: RootState) => state.keplerGl?.map?.visState.datasets,
  (datasets) => datasets
)

export const getLayers = createSelector(
  (state: RootState) => state.keplerGl?.map?.visState.layers,
  (layers) => layers
)

export const getkeplerDatasetById = createSelector(
  (state: RootState) => state.keplerGl?.map?.visState.datasets,
  (_: unknown, datasetId: string) => datasetId,
  (datasets, datasetId) => {
    return datasets && datasetId in datasets ? datasets[datasetId] : null
  }
)

export const getDatasetZoomRange = createSelector(
  (state: RootState) => state.keplerGl?.map?.visState.datasets,
  (_: unknown, datasetId: string) => datasetId,
  (datasets, datasetId) => {
    return datasetId in datasets ? getZoomRangeFromTilesetDataset(datasets[datasetId]) : null
  }
)

const keplerDatasets = (state: RootState) => state.keplerGl.map?.visState?.datasets
const datasets = (state: RootState) => state.maps.currentMapDatasets

export const getEnrichedDatasets = createSelector([keplerDatasets, datasets], (keplerDatasets, datasets) => {
  return Object.keys(keplerDatasets).map((datasetId) => ({
    ...keplerDatasets[datasetId],
    ...datasets.find((d) => d.id === datasetId)
  }))
})

export const getEnrichedDatasetsByDatasetIds = createSelector(
  [getEnrichedDatasets, (_: unknown, datasetIds: string[]) => datasetIds],
  (enrichedDatasets, datasetIds) => {
    return enrichedDatasets.filter((ds) => datasetIds.includes(ds.id))
  }
)

export const getEnrichedDatasetById = createSelector(
  [keplerDatasets, datasets, (_: unknown, datasetId: string) => datasetId],
  (keplerDatasets, datasets, datasetId) => {
    const dataset = datasets.find((d) => d.id === datasetId)
    return datasetId in keplerDatasets ? { ...dataset, ...keplerDatasets[datasetId] } : null
  }
)

export const getDatasetLoadingProgress = createSelector([datasets], (datasets) => {
  const initialPercentage = 10
  if (!datasets.length) return 100
  const n = datasets.filter((d) => d.status === RequestStatus.Fulfilled || d.status === RequestStatus.Rejected).length
  return Math.max(Math.round((n / datasets.length) * 100), initialPercentage)
})

export const getLayerByDatasetId = createSelector(
  (state: RootState) => state.keplerGl?.map?.visState.layers,
  (_: unknown, datasetId: string) => datasetId,
  (layers, datasetId): Layer => {
    return layers.find((layer: Layer) => layer.config.dataId === datasetId)
  }
)

export const datasetHasValidLayerSelector = createSelector(
  (state: RootState) => state.keplerGl?.map?.visState.layers,
  (_: unknown, datasetId: string) => datasetId,
  (layers, datasetId): boolean => {
    return layers.some((layer: Layer) => layer.config.dataId === datasetId && layer.type !== LayerTypes.Unknown)
  }
)

export const getLayersByDatasetId = createSelector(
  (state: RootState) => state.keplerGl?.map?.visState.layers,
  (_: unknown, datasetId: string) => datasetId,
  (layers, datasetId): Layer[] => {
    return layers.filter((layer: Layer) => layer.config.dataId === datasetId)
  }
)

export const getLayerToBeMergedByDatasetId = createSelector(
  (state: RootState) => state.keplerGl?.map?.visState.layerToBeMerged,
  (_: unknown, datasetId: string) => datasetId,
  (layers, datasetId): Layer => {
    return layers.find((layer: Layer) => layer.config.dataId === datasetId)
  }
)

export const getLayerById = createSelector(
  (state: RootState) => state.keplerGl?.map?.visState.layers,
  (_: unknown, layerId: string) => layerId,
  (layers, layerId): Layer => {
    return layers.find((layer: Layer) => layer.id === layerId)
  }
)

export const getDatasetsByType = createSelector(
  (state: RootState) => state.keplerGl?.map?.visState.datasets,
  (_: unknown, type: MapType) => type,
  (datasets, type): KeplerTable[] => Object.values(datasets as KeplerTable[]).filter((d) => d.metadata?.type === type)
)

export const isDatasetLoadingTiles = createSelector(
  (state: RootState) => state.keplerGl.map.visState.layers,
  (_: unknown, datasetId: string) => datasetId,
  (layers, datasetId: string): boolean =>
    layers.filter((layer) => layer.config.dataId === datasetId).some((layer) => layer.isLoadingTiles)
)

export const isMapSplit = createSelector(
  (state: RootState) => state.keplerGl?.map?.visState?.splitMaps,
  (splitMaps) => splitMaps && splitMaps.length > 1
)

const activeSidePanel = (state: RootState) => state.keplerGl?.map?.uiState.activeSidePanel

export const panelLayerActive = createSelector(
  [activeSidePanel],
  (activeSidePanel) => activeSidePanel === LAYER_SIDE_PANEL
)

export default keplerGlReducer
